import type { FC } from 'react';
import { useState, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import SectionHeader from '../SectionHeader/SectionHeader';
import {
  ReportData,
  TimePeriod,
  TimePeriodOption,
} from '../../../types/reports';
import Surface from '@wix/da-ds/pkg/Surface';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import SectionEmptyState from '../SectionEmptyState';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';

import s from './ReportsSection.scss';

interface ReportsSectionProps {
  reportType: string;
  title: string;
  description: string;
  data?: ReportData;
  /**
   * The component automatically uses default time periods if no timePeriodOptions are provided.
   * You can override this by passing custom options:
   *
   * @example
   *   timePeriodOptions={[
   *     { value: TimePeriod.PAST_MONTH, translationKey: 'studio.reports.timePeriod.pastMonth' },
   *     { value: TimePeriod.PAST_YEAR, translationKey: 'studio.reports.timePeriod.pastYear' },
   *     { value: TimePeriod.ALL, translationKey: 'studio.reports.timePeriod.all' }
   *   ]}
   */
  timePeriodOptions?: TimePeriodOption[];
  renderTable: (filteredData: any[], timePeriod?: TimePeriod) => ReactNode;
  dataFilter?: (data: any[]) => any[];
  className?: string;
}

export const ReportsSection: FC<ReportsSectionProps> = ({
  reportType,
  title,
  description,
  data,
  timePeriodOptions,
  renderTable,
  dataFilter,
  className,
}) => {
  const { t } = useTranslation();

  const defaultTimePeriod = useMemo(() => {
    // When timePeriodOptions are provided, default to the first option
    if (timePeriodOptions && timePeriodOptions.length > 0) {
      return timePeriodOptions[0].value;
    }

    // When no timePeriodOptions are provided:
    // Default to PAST_MONTH if there is data for it
    const pastMonthData = data?.timePeriods?.[TimePeriod.PAST_MONTH];
    if (pastMonthData && pastMonthData.length > 0) {
      return TimePeriod.PAST_MONTH;
    }

    // If there is no data for PAST_MONTH, default to ALL
    return TimePeriod.ALL;
  }, [data, timePeriodOptions]);

  const [selectedTimePeriod, setSelectedTimePeriod] =
    useState<TimePeriod>(defaultTimePeriod);

  const unfilteredData =
    data?.timePeriods?.[selectedTimePeriod as TimePeriod] || [];
  const filteredData = dataFilter?.(unfilteredData) || unfilteredData;
  const hasData = filteredData.length > 0;

  return (
    <Surface
      type={ThemeSurface.SECONDARY}
      tag="section"
      themeOverrideOnly
      className={c(s['root'], className)}
    >
      <BiLoggerContextProvider value={{ sectionname: reportType }}>
        <SectionHeader
          title={title}
          description={description}
          selectedTimePeriod={selectedTimePeriod}
          onTimePeriodChange={setSelectedTimePeriod}
          timePeriodOptions={timePeriodOptions}
        />
        {hasData && renderTable(filteredData, selectedTimePeriod)}
        {!hasData && (
          <SectionEmptyState
            title={t(
              `studio.reports.section.empty.${selectedTimePeriod}.title`
            )}
            body={t(`studio.reports.section.empty.body`)}
          />
        )}
      </BiLoggerContextProvider>
    </Surface>
  );
};

export default ReportsSection;
